import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./NotificationManagement.css";
import paperClip from "../../assets/paper-clip.png";
import { addNewNotification, getAllDistributionList } from "../../services/svt/foundation-service";
import Loader from "../common/Loader/Loader";
import validateFileFormat from '../../utils/ValidationFileFormat';
import validateFields from "../../utils/ValidationInput";
import { toast } from 'react-toastify';

const NotificationManageMent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    distributionList: [],
    emailIds: "",
    subject: "",
    text: "",
    files: [],
  });
  const [loading, setLoading] = useState(false);
  const [distributionLists, setDistributionLists] = useState([]);

  useEffect(() => {
    fetchDistributionList();
  }, []);

  const fetchDistributionList = async () => {
    setLoading(true);
    try {
      const response = await getAllDistributionList();
      if (response?.data) {
        const data = response?.data.map((item) => ({
          ...item,
          value: item.id,
          label: item.title
        }));
        setDistributionLists(data);
      }
    } catch (err) {
      console.error("Error fetching distribution lists:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "files" && files) {
      const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
      const validFiles = [];

      Array.from(files).forEach((file) => {
        // Check file size
        if (file.size > maxSizeInBytes) {
          toast.error(t("fileSizeExceedsLimitError", { name: file.name, size: "5MB" }), { position: "top-center", theme: "dark" });
          return;
        }
        // Check file format using utility function
        if (!validateFileFormat(file)) {
          return;
        }
        validFiles.push(file);
      });

      // Add only valid files
      if (validFiles.length > 0) {
        setFormData((prev) => ({
          ...prev,
          files: [...prev.files, ...validFiles],
        }));
      }
      // Clear the input
      e.target.value = '';
    } else {
      toast.dismiss();
      if (name === "text" && value.length > 8000) {
        toast.error(t("emailTextExceedsMaxLengthError", { number: 8000 }), {
          position: "top-center",
          theme: "dark",
        });
        return;
      }

      if (name === "subject" && value.length > 255) {
        toast.error(t("emailSubjectExceedsMaxLengthError", { number: 255 }), {
          position: "top-center",
          theme: "dark",
        });
        return;
      }

      let processedValue = value;
      if (name === "text") {
        // Replace newlines with escaped <br>
        // processedValue = value.replace(/\n/g, '(br)');
        processedValue = value;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
    }
  };

  const removeFile = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleMultiSelectChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      distributionList: selected ? selected.map(item => item.value) : [],
    }));
  };

  const onClearForm = () => {
    setFormData({
      distributionList: [],
      emailIds: "",
      subject: "",
      text: "",
      files: [],
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Required fields with types (text or email)
    const requiredFields = [
      {
        label: t("emailSubject"),
        key: "Email Subject",
        type: "text",
        name: "subject",
        charLength: 255,
        value: formData.subject,
      },
      {
        label: t("emailText"),
        key: "Email Text",
        type: "",
        name: "text",
        charLength: 8000,
        value: formData.text,
      }
    ];

    const isValid = validateFields(
      formData,
      requiredFields,
      "byValue"
    );
    if (!isValid) {
      setLoading(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("TO", "");
    formDataToSend.append("CC", "");
    formDataToSend.append("BCC", formData.emailIds);
    formDataToSend.append("EmailSubject", formData.subject);
    // Convert line breaks to \n characters and pass as raw text
    const emailTextWithLineBreaks = formData.text.replace(/\r?\n/g, '\\n');
    formDataToSend.append("EmailText", emailTextWithLineBreaks);
    // Append each file individually as binary data
    formData.files.forEach((file) => {
      formDataToSend.append("Attachments", file);
    });
    formData.distributionList.forEach((id) => {
      formDataToSend.append("DistributionListIds", id);
    });

    try {
      const fileResp = await addNewNotification(formDataToSend);
      if (fileResp?.data) {
        setLoading(false);
        onClearForm();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return loading ? (
    <Loader />
  ) : (
    <div className="notification-container">
      <h3 className="notification-title">{t("notifications")}</h3>
      <hr style={{ marginBottom: "1.2rem" }}></hr>
      <form className="notification-form">
        <div className="form-group">
          <label className="form-label">{t("selectDistList")}:</label>
          <div className="form-row">
            <div className="form-select reduced-width">
              <Select
                isMulti
                options={distributionLists}
                value={distributionLists.filter(option => formData.distributionList.includes(option.value))}
                onChange={handleMultiSelectChange}
                placeholder={t("selectDistributionList")}
                classNamePrefix="react-select"
                components={{ DropdownIndicator: () => null }}
              />
            </div>
          </div>

          <div className="form-file">
            <label htmlFor="file-upload" className="file-label">
              <label className="form-label">{t("attachFile")}</label>
              <span className="file-icon" role="img" aria-label="attach">
                <img
                  src={paperClip}
                  alt={t("attachIconAlt")}
                  className="file-icon-img"
                />
              </span>
              <input
                id="file-upload"
                name="files"
                type="file"
                multiple
                className="file-input"
                onChange={handleInputChange}
              />
            </label>

            <div className="filesOuterDiv">
              {formData.files.length > 0 && (
                <div className="files-list">
                  {formData.files.map((file, index) => (
                    <div key={index} className="file-item">
                      <span className="file-name">{file.name}</span>
                      <div onClick={() => removeFile(index)} className="remove-file-btn">
                        x
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <label className="form-label">{t("recipientsEmailId")}:</label>
          <textarea
            type="text"
            name="emailIds"
            placeholder={t("typeEmailIds")}
            value={formData.emailIds}
            onChange={handleInputChange}
            className="textarea-field"
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t("emailSubject")}: <span className="required-indicator">*</span></label>
          <input
            type="text"
            name="subject"
            placeholder={t("typeEmailSubject")}
            value={formData.subject}
            onChange={handleInputChange}
            className="input-field"
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t("emailText")}: <span className="required-indicator">*</span></label>
          <textarea
            name="text"
            placeholder={t("typeEmailText")}
            value={formData.text}
            // value={formData.text.replace(/\(br\)/g, '\n')}
            onChange={handleInputChange}
            className="textarea-field"
            rows="7"
            required
          />
        </div>
        <div className="form-actions">
          <button type="button" className="saveButton" onClick={handleSend}>
            {t("send")}
          </button>
          <button type="button" onClick={handleCancel} className="cancelButton">
            {t("cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NotificationManageMent;